/**
 * @module UseAnalysisArtefacts
 * @purpose Hooks for fetching the cursor-paginated artefact canvas and associated facets.
 * @depends listArtefacts, getFacets (services/analysis-artefacts), useInvalidationSubscription
 * @dependedOnBy pages/case/analysis — AnalysisCanvasPage and related panels
 *
 * @rules
 * - Performance: stale-request guard (requestIdRef) prevents out-of-order responses overwriting newer pages
 * - State: local useState; no context needed
 * - Errors: errors set as string | null; service propagates ApiError from apiClient
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { getFacets, listArtefacts } from '../services/analysis-artefacts'
import type {
  AnalysisArtefact,
  AnalysisFacets,
  AnalysisFilters,
} from '../types/analysis-artefacts'
import { useInvalidationSubscription } from './use-list-invalidation'

const DEFAULT_PAGE_SIZE = 200

// =============================================================================
// useAnalysisArtefacts
// =============================================================================

export interface UseAnalysisArtefactsParams {
  caseId: string | null
  search: string
  filters: AnalysisFilters
  pageSize?: number
}

export interface UseAnalysisArtefactsResult {
  items: AnalysisArtefact[]
  nextCursor: string | null
  loading: boolean
  loadingMore: boolean
  error: string | null
  refetch: () => Promise<void>
  loadMore: () => Promise<void>
}

/**
 * Cursor-paginated artefact list with filter, search and loadMore support.
 *
 * @behaviour Resets items and re-fetches first page whenever caseId, search, or filters change.
 *            Appends items on loadMore. Stale-request guard prevents out-of-order writes.
 *            Subscribes to ingestion_runs and qc_runs invalidation channels for auto-refresh.
 * @errors Sets error string on ApiError; clears on next successful fetch.
 */
export function useAnalysisArtefacts(
  params: UseAnalysisArtefactsParams,
): UseAnalysisArtefactsResult {
  const { caseId, search, filters, pageSize = DEFAULT_PAGE_SIZE } = params

  const [items, setItems] = useState<AnalysisArtefact[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Serialise filters to a stable dep value — AnalysisFilters contains
  // array fields (artefactType[], endpointId[], …) that are themselves
  // unstable references. Serialising prevents the infinite-fetch loop while
  // still responding to actual filter changes.
  const filtersKey = JSON.stringify(filters)

  // Stale-request guard: each call to `fetch` increments the counter;
  // only the response matching the latest id is applied.
  const requestIdRef = useRef(0)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setItems([])
      setNextCursor(null)
      setLoading(false)
      setError(null)
      return
    }

    requestIdRef.current += 1
    const thisId = requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      // Pass `filters` through (not a reconstructed literal) so the service
      // receives the caller's original shape. `filtersKey` is the dep sentinel.
      const page = await listArtefacts(caseId, { limit: pageSize, search, filters })
      if (thisId !== requestIdRef.current) return
      setItems(page.items)
      setNextCursor(page.nextCursor)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to fetch artefacts')
      setItems([])
      setNextCursor(null)
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
    // Intentionally omits `filters` (unstable object reference) and `params`;
    // we depend on `filtersKey` (serialised) and scalar primitives instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, search, filtersKey, pageSize])

  const loadMore = useCallback(async () => {
    if (!caseId || !nextCursor || loadingMore) return

    setLoadingMore(true)
    setError(null)

    try {
      const page = await listArtefacts(caseId, {
        cursor: nextCursor,
        limit: pageSize,
        search,
        filters,
      })
      setItems((prev) => [...prev, ...page.items])
      setNextCursor(page.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more artefacts')
    } finally {
      setLoadingMore(false)
    }
    // Intentionally omits `filters` (unstable); depends on `filtersKey` and scalars.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, nextCursor, loadingMore, search, filtersKey, pageSize])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('ingestion_runs', fetch)
  useInvalidationSubscription('qc_runs', fetch)

  return { items, nextCursor, loading, loadingMore, error, refetch: fetch, loadMore }
}

// =============================================================================
// useAnalysisFacets
// =============================================================================

export interface UseAnalysisFacetsParams {
  caseId: string | null
  search: string
  filters: AnalysisFilters
}

export interface UseAnalysisFacetsResult {
  facets: AnalysisFacets | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Facet aggregation for the analysis canvas filter panel.
 *
 * @behaviour Fetches and replaces facets atomically on caseId, search, or filter change.
 *            Subscribes to ingestion_runs and qc_runs invalidation channels for auto-refresh.
 * @errors Sets error string on ApiError; clears on next successful fetch.
 */
export function useAnalysisFacets(params: UseAnalysisFacetsParams): UseAnalysisFacetsResult {
  const { caseId, search, filters } = params

  const [facets, setFacets] = useState<AnalysisFacets | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Serialise filters — same rationale as useAnalysisArtefacts above.
  const filtersKey = JSON.stringify(filters)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setFacets(null)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `filters` through (not a reconstructed literal); `filtersKey` is the dep sentinel.
      const data = await getFacets(caseId, { search, filters })
      setFacets(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch facets')
      setFacets(null)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `filters` (unstable object reference); depends on `filtersKey` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, search, filtersKey])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('ingestion_runs', fetch)
  useInvalidationSubscription('qc_runs', fetch)

  return { facets, loading, error, refetch: fetch }
}
